import { useState } from 'react';

import { ButtonBase, Typography } from '@mui/material';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { Status } from './components/Status';
import styles from './MenuListItem.module.scss';
import type { MenuItem } from './types';
import { Icon } from '@/components/ui/Icon';
import { uiIcons } from '@/constants/uiIcons';
import { useIsMobile } from '@/hooks/useIsMobile';

interface MenuListItemProps {
  id: string;
  label: MenuItem['label'];
  iconName?: MenuItem['icon'];
  to?: MenuItem['path'];
  onClick?: () => void;
  isActive?: boolean;
  isActiveMenuListParent?: boolean;
  disabled?: boolean;
  isExpandable?: boolean;
  isExpanded?: boolean;
  className?: string;
  isChildItem?: boolean;
  tabIndex?: number;
  target?: string;
  status?: MenuItem['status'];
}

export const MenuListItem = ({
  label,
  iconName = '',
  to,
  onClick,
  isActive = false,
  isActiveMenuListParent = false,
  disabled = false,
  isExpandable = false,
  isExpanded = false,
  className = '',
  isChildItem = false,
  tabIndex = 0,
  target,
  status,
  id,
}: MenuListItemProps) => {
  const { t } = useTranslation('navigation');
  const isMobile = useIsMobile();

  const [hovered, setHovered] = useState(false);

  const content = (
    <>
      {iconName && (
        <Icon
          name={iconName}
          weight={hovered || isActive || isActiveMenuListParent ? 400 : 300}
        />
      )}
      <Typography variant="bodyRegular" component="span">
        {t(label)}
      </Typography>
      {status && <Status className={styles.MenuListItemStatus} type={status} />}
      {target === '_blank' && (
        <span className={styles.AdditionalIcon} aria-hidden="true">
          <Icon
            name={uiIcons.navigation.openExternal}
            weight={hovered || isActive ? 400 : 300}
          />
        </span>
      )}
      {isExpandable && (
        <span
          className={cx(styles.AdditionalIcon, styles.MenuListItemExpandIcon, {
            [styles.MenuListItemExpandIconActive]: isExpanded,
          })}
          aria-hidden="true"
        >
          <Icon
            name={uiIcons.controls.expandDown}
            weight={hovered || isActive || isActiveMenuListParent ? 400 : 300}
          />
        </span>
      )}
    </>
  );

  return (
    <>
      {!isExpandable && to && to !== '' ? (
        <NavLink
          className={cx(styles.MenuListItem, className, {
            [styles.MenuListItemActive]: isActive,
            [styles.MenuListChildItem]: isChildItem,
          })}
          to={to}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          tabIndex={tabIndex}
          target={target}
          data-automation-id={`MainNavigation${id}${isChildItem ? 'Child' : 'Parent'}Link`}
        >
          {content}
        </NavLink>
      ) : (
        <>
          <ButtonBase
            onClick={onClick}
            disabled={disabled}
            className={cx(styles.MenuListItem, className, {
              [styles.MenuListItemActive]: isActive,
              [styles.MenuListParentItemActive]: isActiveMenuListParent,
            })}
            aria-expanded={isExpanded}
            aria-controls={`collapsible-menu-${label.toLowerCase()}${isMobile ? '-mobile' : ''}`}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            tabIndex={tabIndex}
            data-automation-id={`MainNavigation${id}ParentButton`}
          >
            {content}
          </ButtonBase>
        </>
      )}
    </>
  );
};
